import {
    defaultMemberEducationTrack,
    isMemberEducationStep,
    isMemberEducationTrack,
    MemberEducationStep,
    MemberEducationTrack,
    memberEducationTrackSchema,
} from '@/api/mem/memberEducation';
import { memberEducationStepsHierarchyQueryOptions, useCreateMemberEducationTrackMutation, useDeleteMemberEducationStepMutation, useDeleteMemberEducationTrackMutation, useUpdateMemberEducationStepMutation, useUpdateMemberEducationTrackMutation } from '@/api/mem/memberEducationQueryOptions';
import { completeScoutIdEducationApprover } from '@/api/members';
import { OrganizationReferenceType } from '@/api/organizations';
import { fetchDetailPermissionsQueryOptions } from '@/api/permissionsQueryOptions';
import CreateDetailAccordion, { useCreateDetail } from '@/components/detail/CreateDetailRecord';
import useDetailCrudEvents from '@/components/hooks/useDetailCrudEvents';
import { usePersistedVisibilityModel } from '@/components/hooks/usePersistedVisibilityModel';
import useResponsive from '@/components/hooks/useResponsive';
import LoadProgress from '@/components/LoadProgress';
import { DetailGridToolbar } from '@/components/mui-x/datagrid/DetailGridToolbar';
import MemberReferenceEditCell from '@/components/mui-x/datagrid/MemberReferenceEditCell';
import ScoregDatePicker from '@/components/mui/ScoregDatePicker';
import SelectEducationTrack from '@/components/select/SelectEducationTrack';
import MemberEducationAddStepDialog from '@/dialog/MemberEducationAddStepDialog';
import { getCurrentMember } from '@/store/nav/navSlice';
import { yupResolver } from '@hookform/resolvers/yup';
import AddBoxIcon from '@mui/icons-material/AddBox';
import CancelIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import { Grid } from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { DataGridPremium, GridActionsCellItem, GridColDef, GridColumnVisibilityModel, GridEditInputCell, GridEditInputCellProps, GridRenderCellParams, GridRowId, GridRowModes, GridRowModesModel } from '@mui/x-data-grid-premium';
import { deDE } from '@mui/x-data-grid-premium/locales';
import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import dayjs from 'dayjs';
import { t } from 'i18next';
import React from 'react';
import { useSelector } from 'react-redux';

export const Route = createFileRoute(
    '/_auth/member_/$memberUuid/education',
)({
    component: MemberEducationComponent,
})

function MemberEducationComponent() {
    const params = Route.useParams()
    const [addStepDialogOpen, setAddStepDialogOpen] = React.useState(false)
    const [selectedRow, setSelectedRow] = React.useState<any>()

    const memberEducationStepsQuery = useSuspenseQuery(
        memberEducationStepsHierarchyQueryOptions(params.memberUuid),
    )
    const rows = memberEducationStepsQuery.data

    const permissionsQuery = useSuspenseQuery(
        fetchDetailPermissionsQueryOptions('member', params.memberUuid, [
            'MemberEducationStep',
        ]),
    )
    const permissions = permissionsQuery.data

    const createable =
        permissions &&
        permissions.filter(
            (p) =>
                p.detail === 'MemberEducationStep' && p.permissions.some((a) => a === 'create'),
        ).length > 0

    const trackCrud = useDetailCrudEvents<MemberEducationTrack>('memberEducationTrack')

    const createMemberEducationTrackMutation = useCreateMemberEducationTrackMutation(
        params.memberUuid,
        trackCrud.onCreated,
        trackCrud.onError,
    )

    const updateMemberEducationTrackMutation = useUpdateMemberEducationTrackMutation(
        params.memberUuid,
        trackCrud.onUpdated,
        trackCrud.onError,
    )

    const deleteMemberEducationTrackMutation = useDeleteMemberEducationTrackMutation(
        params.memberUuid,
        trackCrud.onDeleted,
        trackCrud.onError,
    )

    const stepCrud = useDetailCrudEvents<MemberEducationStep>('memberEducationStep')

    // const createMemberEducationStepMutation = useCreateMemberEducationStepMutation(
    //   params.memberUuid,
    //   stepCrud.onCreated,
    //   stepCrud.onError,
    // )
    const updateMemberEducationStepMutation = useUpdateMemberEducationStepMutation(
        params.memberUuid,
        stepCrud.onUpdated,
        stepCrud.onError,
    )
    const deleteMemberEducationStepMutation = useDeleteMemberEducationStepMutation(
        params.memberUuid,
        stepCrud.onDeleted,
        stepCrud.onError,
    )

    const [dialogOpen, setDialogOpen] = React.useState(false);

    const { medium, large, xlarge } = useResponsive();

    const handleEditClick = (id: GridRowId) => () => {
        console.log('handleEditClick');
        setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
    };

    const handleDeleteClick = (id: GridRowId, row: any) => () => {
        console.log('handleDeleteClick on ' + id);
        setSelectedRow(row);
        setDialogOpen(true);
    };

    const handleSaveClick = (id: GridRowId) => () => {
        console.log('handleSaveClick');

        setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
    };

    const handleCancelClick = (id: GridRowId) => () => {
        console.log('handleCancelClick');
        setRowModesModel({
            ...rowModesModel,
            [id]: { mode: GridRowModes.View, ignoreModifications: true },
        });
    };

    const handleAddStepClick = (_id: GridRowId, row: any) => () => {
        setAddStepDialogOpen(true)
        setSelectedRow(row)
    };

    const handleCancel = () => {
        setDialogOpen(false);
    }

    const handleDelete = () => {
        console.log('handleDelete ' + selectedRow.uuid);
        setDialogOpen(false);
        if (selectedRow.memberEducationTrack) {
            deleteMemberEducationStepMutation.mutate(selectedRow);
        } else {
            deleteMemberEducationTrackMutation.mutate(selectedRow);
        }
    }

    const processRowUpdate = (row: any) => {
        if (row.memberEducationTrack) {
            updateMemberEducationStepMutation.mutate(row)
        } else {
            updateMemberEducationTrackMutation.mutate(row)
        }

        return row;
    };

    const [rowModesModel, setRowModesModel] = React.useState<GridRowModesModel>({});

    const columns: GridColDef[] = []
    columns.push({
        field: 'description',
        minWidth: 100,
        headerName: t('property.memberEducationTrack.description'),
        editable: true,
    })
    if ((large || xlarge)) {
        columns.push({
            field: 'startDate',
            width: 125,
            type: 'date',
            headerName: t('property.memberEducationTrack.startDate'),
            valueGetter: (value) => {
                return value && new Date(value)
            },
            editable: true,
        })
    }
    columns.push({
        field: 'completeDate',
        width: 125,
        type: 'date',
        headerName: t('property.memberEducationStep.completeDate'),
        valueGetter: (value) => {
            return value && new Date(value)
        },
        editable: true,
    })

    columns.push({
        field: 'approveMember',
        minWidth: 125,
        renderCell: (params: GridRenderCellParams) => {
            if (params.row.approveMember) {
                return <>{params.row.approveMember.firstname} {params.row.approveMember.lastname} ({params.row.approveMember.scoutId})</>
            }
            return <></>
        },
        renderEditCell: (params: GridRenderCellParams) => {
            if (params.row.memberEducationTrack) {
                return <MemberReferenceEditCell {...params} complete={completeScoutIdEducationApprover} />
            } else {
                return <></>
            }
        },
        headerName: t('property.memberEducationStep.approveMember'),
        editable: true,
        filterable: false,
        sortable: false,
    })
    function renderApprover(params: GridEditInputCellProps) {
        return <GridEditInputCell {...params} disabled={!params.row.memberEducationTrack} />;
    }
    columns.push({
        field: 'approver',
        minWidth: 125,
        headerName: t('property.memberEducationStep.approver'),
        valueGetter: (value) => {
            return value
        },
        renderEditCell: renderApprover,
        editable: true,
    })
    columns.push({
        field: 'approveOrganization',
        minWidth: 125,
        headerName: t('property.memberEducationStep.approveOrganization'),
        valueGetter: (value: OrganizationReferenceType) => {
            return value && value.name
        },
        editable: false,
    })
    columns.push({
        field: 'comment',
        minWidth: 300,
        headerName: t('property.general.comment'),
        editable: true,
    })


    if ((medium || large || xlarge)) {
        columns.push(
            {
                field: 'detailActions',
                type: 'actions',
                headerName: t('entity.educationStep'),
                width: 50,
                cellClassName: 'actions',

                getActions: ({ id, row }: { id: GridRowId, row: MemberEducationStep | MemberEducationTrack }) => {
                    const writeable = row.permissions ? row.permissions.some(perm => perm === 'write') : false;
                    if (isMemberEducationTrack(row) && row.educationTrack.manual) {
                        return [
                            <GridActionsCellItem
                                icon={<AddBoxIcon />}
                                label='Add'
                                key={`add-${id}`}
                                disabled={!writeable}
                                onClick={handleAddStepClick(id, row)}
                            />
                        ]
                    } else {
                        return []
                    }
                },
            }
        )
        columns.push(
            {
                field: 'actions',
                type: 'actions',
                headerName: t('column.actions'),
                width: 100,
                cellClassName: 'actions',

                getActions: ({ id, row }: { id: GridRowId, row: MemberEducationStep | MemberEducationTrack }) => {
                    const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;
                    const writeable = row.permissions ? row.permissions.some(perm => perm === 'write') : false;
                    const deleteable = row.permissions ? row.permissions.some(perm => perm === 'delete') : false;

                    if (isInEditMode) {
                        return [
                            <GridActionsCellItem
                                icon={<SaveIcon />}
                                label='Save'
                                key={`save-${id}`}
                                style={{
                                    color: 'primary.main',
                                }}
                                onClick={handleSaveClick(id)}
                            />,
                            <GridActionsCellItem
                                icon={<CancelIcon />}
                                label='Cancel'
                                key={`cancel-${id}`}
                                className='textPrimary'
                                onClick={handleCancelClick(id)}
                                color='inherit'
                            />,
                        ];
                    }

                    const actions = [];
                    actions.push(
                        <GridActionsCellItem
                            icon={<EditIcon />}
                            label='Edit'
                            key={`edit-${id}`}
                            disabled={!writeable}
                            className='textPrimary'
                            onClick={handleEditClick(id)}
                            color='inherit'
                        />
                    )
                    if ((isMemberEducationTrack(row)) ||
                        isMemberEducationStep(row) && row.memberEducationTrack.educationTrack.manual) {
                        actions.push(
                            <GridActionsCellItem
                                icon={<DeleteIcon />}
                                label='Delete'
                                key={`delete-${id}`}
                                disabled={!deleteable}
                                className='textPrimary'
                                onClick={handleDeleteClick(id, row)}
                                color='inherit'
                            />
                        )
                    }

                    return actions
                }
            }
        )
    }

    columns.push({
        field: 'trackCompleteDate',
        minWidth: 100,
        type: 'date',
        headerName: t('property.memberEducationTrack.completeDate'),
        valueGetter: (_value, row) => {
            if (isMemberEducationTrack(row)) {
                return row.completeDate && new Date(row.completeDate)
            }
            return undefined
        },
        editable: false,
    })

    const initialVisibilityModel = {
        'description': false,
        'startDate': false,
        'completeDate': true,
        'approveMember': true,
        'approver': true,
        'approveOrganization': true,
        'comment': false,
        'trackCompleteDate': false,
    } as GridColumnVisibilityModel

    const [visibilityModel, setVisibilityModel] = usePersistedVisibilityModel('memberEducation', initialVisibilityModel)
    const handleVisibilityModelChange = React.useCallback((model: GridColumnVisibilityModel) => {
        setVisibilityModel(model)
    }, [setVisibilityModel]);

    const groupingColDef = {
        headerName: t('property.educationTrack.name'),
        minWidth: 200,
        valueGetter: (value: string, row: any) => {
            if (row.memberEducationTrack && row.educationStep) {
                return row.educationStep.code + ' - ' + row.educationStep.name
            } else if (row.memberEducationTrack) {
                return row.description
            } else {
                return row.educationTrack.code + ' - ' + row.educationTrack.name + (row.description ? ' [' + row.description + ']' : '')
            }
            return value
        },
    }

    const onSubmitTrack = (data: MemberEducationTrack) => {
        createMemberEducationTrackMutation.mutate(data)
    }

    const createMemberEducationTrack = useCreateDetail<MemberEducationTrack>({
        onSubmit: onSubmitTrack,
        resolver: yupResolver<MemberEducationTrack, any, any>(memberEducationTrackSchema),
        defaultValues: {
            ...defaultMemberEducationTrack,
            member: {
                uuid: params.memberUuid,
            },
        },
        title: t('action.assign', { entity: t('entity.educationTrack') }),
    })

    const currentMember = useSelector(getCurrentMember)

    const CustomToolbar = () => {
        return (
            <DetailGridToolbar scope='education' getScopeName={() => { return currentMember.firstname + "_" + currentMember.lastname }} />
        )
    }

    const responsive = useResponsive()
    const defaultLocaleText = deDE.components.MuiDataGrid.defaultProps.localeText

    const localeText = responsive.minMedium ? defaultLocaleText : { ...defaultLocaleText, ...{ toolbarColumns: '', toolbarFilters: '' } }


    if (
        memberEducationStepsQuery.isLoading ||
        memberEducationStepsQuery.isFetching
    ) {
        return <LoadProgress />
    }

    return (
        <>
            <Dialog
                open={dialogOpen}
                onClose={handleCancel}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    {"Eintrag löschen?"}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        {t('message.deleteAreYouSure')}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancel}>Abbrechen</Button>
                    <Button onClick={handleDelete} autoFocus variant="contained" color="error">
                        Löschen
                    </Button>
                </DialogActions>
            </Dialog>
            {createable && (
                <CreateDetailAccordion {...createMemberEducationTrack}>
                    <Grid container spacing={2}>
                        <input
                            type='hidden'
                            defaultValue={params.memberUuid}
                            {...createMemberEducationTrack.form.register('member.uuid')}
                        />
                        <Grid
                            size={{
                                xs: 12,
                                md: 6,
                                lg: 4
                            }}>
                            <SelectEducationTrack control={createMemberEducationTrack.form.control} name="educationTrack.uuid" />
                        </Grid>
                        <Grid
                            size={{
                                xs: 12,
                                md: 6,
                                lg: 4
                            }}>
                            <ScoregDatePicker
                                name='startDate'
                                defaultValue={dayjs(defaultMemberEducationTrack.startDate)}
                                disabled={false}
                                control={createMemberEducationTrack.form.control}
                                label={t('property.general.startDate')}
                            />
                        </Grid>
                        <></>
                    </Grid>
                </CreateDetailAccordion>
            )}
            <MemberEducationAddStepDialog open={addStepDialogOpen} setOpen={setAddStepDialogOpen} memberUuid={params.memberUuid} row={selectedRow} />
            <DataGridPremium
                treeData
                getTreeDataPath={(row) => row.memberEducationTrack ? [row.memberEducationTrack.uuid, row.uuid] : [row.uuid]}
                groupingColDef={groupingColDef}
                rows={rows}
                getRowId={(row) => { return row.uuid || '' }}
                columns={columns}
                initialState={{
                    filter: {
                        filterModel: {
                            items: [
                                { field: 'trackCompleteDate', operator: 'isEmpty' },
                            ],
                        }
                    },
                }}
                columnVisibilityModel={visibilityModel}
                onColumnVisibilityModelChange={handleVisibilityModelChange}
                localeText={localeText}
                defaultGroupingExpansionDepth={2}
                processRowUpdate={(updatedRow) => processRowUpdate(updatedRow)}
                onProcessRowUpdateError={(error) => console.log(error)}
                editMode='row'
                rowModesModel={rowModesModel}
                hideFooter={true}
                disableMultipleRowSelection
                disableAggregation
                disableDensitySelector
                slots={{ toolbar: CustomToolbar }}
                density='compact'
                showToolbar />
        </>
    );
}
